import * as React  from "react";
import {UseRef}    from "@Common/react/use-ref";
import {cx}        from "emotion";

import "./commentBoxWithCounter.scss";

import {cs}                     from "@Common/react/chain-services";
import {UseState}               from "@Common/react/use-state";
import {Types}                  from "@Common/types";
import {ButtonSpinner}          from "@Common/ui-components/loading-spinners/button-spinner";
import {FieldOptions}           from "@Common/ui-components/field-options/fieldOptions";


export const CommentBoxWithCounter = ({
    idOrder,
    fieldData,
    fieldValue,
    setFieldValue,
    enableLimitOverride     = false,
    aiTextGenerating,
    setAiTextGenerating,
    companyPrefs,
    replaceStr,
}:{
    idOrder                 : string,
    fieldData               : any,
    fieldValue              : string,
    setFieldValue           : (value:string) => void,
    enableLimitOverride    ?: boolean,
    aiTextGenerating        : boolean,
    setAiTextGenerating     : (value:boolean) => void,
    companyPrefs            : Types.Apis.Models.Company["prefs"],
    replaceStr              : any,
}) => cs(

    ["fieldRef", ({}, next) => UseRef({next})],
    ["enableAiTools", ({}, next) => next(companyPrefs.suggestedCommentsMode !== 0)],
    ["showCharCountModal", ({}, next) => UseState({
        initValue: false,
        next
    })],
    ["overrideCKValue", ({}, next) => UseState({
        initValue: (fieldData.maxLength - replaceStr(fieldValue).length) < 0 ? true : false,
        next
    })],
    ["modalMessage", ({}, next) => UseState({
        initValue: "",
        next
    })],
    ["okCallback", ({}, next) => UseState({
        initValue: () => {null},
        next
    })],
    ["cancelCallback", ({}, next) => UseState({
        initValue: () => {null},
        next
    })],
    ["modalCancelButton", ({}, next) => UseState({
        initValue: true,
        next
    })],
    ["overrideCheckHandler", ({overrideCKValue, modalMessage, okCallback, modalCancelButton, cancelCallback, showCharCountModal}, next) => next( () => {
        if (!overrideCKValue.value) {
            modalMessage.onChange('If you would like to OVERRIDE the character counter and add additional text, please click OK.\r\n\r\nYour proof will display EXACTLY how your text will appear.\r\n\r\nNO CREDITS WILL BE GIVEN TOWARDS ORDERS THAT WERE PLACED WITH ADDITIONAL TEXT.\r\n');
            okCallback.onChange(() => {overrideCKValue.onChange(true); showCharCountModal.onChange(false)});
            cancelCallback.onChange(() => {showCharCountModal.onChange(false)});
            modalCancelButton.onChange(true);
            return showCharCountModal.onChange(true);
        } else if (fieldValue.length > fieldData.maxLength) {
            modalMessage.onChange(`Override will be deactivated. Text in field "${fieldData.displayLabel}" will be cut off to ${fieldData.maxLength} characters`);
            okCallback.onChange(() => {
                showCharCountModal.onChange(false);
                overrideCKValue.onChange(false);
                setFieldValue(fieldValue.slice(0, fieldData.maxLength));
            });
            cancelCallback.onChange(() => {showCharCountModal.onChange(false)});
            modalCancelButton.onChange(true);
            return showCharCountModal.onChange(true);
        } else {
            overrideCKValue.onChange(!overrideCKValue.value);
        }
    })],
    ["showAiIcon", ({enableAiTools}, next) => UseState({
        initValue: enableAiTools,
        next
    })],

    ({fieldRef, enableAiTools, overrideCKValue, overrideCheckHandler, modalMessage, okCallback, modalCancelButton, cancelCallback, showAiIcon, showCharCountModal}) => (
        <div className="comment-box-with-counter-786">
            <input
                name={fieldData.fieldKey}
                data-multipartfield="label"
                value={fieldData.displayLabel}
                hidden={true}
                readOnly={true}
            />
            <FieldOptions
                reference={fieldRef}
                idOrder={idOrder}
                fieldData={fieldData}
                allowAiTools={enableAiTools}
                aiOptions={enableAiTools && {
                    currentValue: fieldValue,
                    maxChars: fieldData.maxLength,
                    allowMaxCharsOverride: overrideCKValue.value,
                    onSetValue: (value) => {
                        setFieldValue(value);
                    },
                    onSetLoading: setAiTextGenerating,
                }}
                allowSuggestedComments={fieldData.allowSuggestedComments && [0,2].includes(companyPrefs.suggestedCommentsMode)}
                allowVdp={false}
                charOverrideModalOptions={{
                    showCharCountModal: showCharCountModal,
                    modalMessage: modalMessage,
                    okCallback: okCallback,
                    cancelCallback: cancelCallback,
                    cancelButton: modalCancelButton,
                    enableLimitOverride: fieldData.enableLimitOverride,
                    onSetValue: (value) => {
                        setFieldValue(value);
                    },

                }}
                charCount={fieldValue.length}
                maxChars={fieldData.maxLength}
                maxCharsExceeded={fieldValue.length > fieldData.maxLength}
                allowOverride={fieldData.enableLimitOverride}
                overrideCKValue={fieldData.enableLimitOverride && overrideCKValue}
                overrideCheckHandler={fieldData.enableLimitOverride && overrideCheckHandler}
            >
                {aiTextGenerating && <div className="ai-text-loading-overlay"><ButtonSpinner size={50} spinnerColor="white"/></div>}
                <textarea
                    id={fieldData.fieldKey}
                    name={fieldData.fieldKey}
                    className={cx("text-area", {countable: fieldData.fieldFormatName === 'countable'})}
                    tabIndex={0}
                    data-fieldname={fieldData.displayLabel}
                    data-multipartfield="data"
                    xd-qa-name={fieldData.fieldKey}
                    xd-qa-type="textarea"
                    value={fieldValue}
                    rows={8}
                    cols={43}
                    ref={ref => fieldRef.set(ref)}
                    onFocus={() => {
                        showAiIcon.onChange(false);
                    }}
                    onBlur={() => {
                            showAiIcon.onChange(true);
                    }}
                    onChange={(e) => {
                        let newVal = e.target.value
                        if (newVal.length > fieldData.maxLength) {
                            if (!enableLimitOverride) {
                                setFieldValue(newVal.slice(0,(fieldData.maxLength - newVal.length)));
                                modalMessage.onChange('You are trying to paste/enter text with length more than allowed.');
                                okCallback.onChange(() => {showCharCountModal.onChange(false)});
                                cancelCallback.onChange(() => {showCharCountModal.onChange(false)});
                                modalCancelButton.onChange(false);
                                return showCharCountModal.onChange(true);
                            } else if (!overrideCKValue.value) {
                                modalMessage.onChange('You are trying to paste/enter text with length more than allowed. Activate override?');
                                okCallback.onChange(() => {
                                    overrideCKValue.onChange(true);
                                    setFieldValue(newVal);
                                    showCharCountModal.onChange(false)
                                });
                                cancelCallback.onChange(() => {
                                    setFieldValue(newVal.slice(0, fieldData.maxLength));
                                    showCharCountModal.onChange(false)
                                });
                                modalCancelButton.onChange(true);
                                return showCharCountModal.onChange(true);
                            }
                        }
                        setFieldValue(e.target.value);
                    }}
                />
                {(showAiIcon.value && !fieldValue.length) ?
                    <div className="sparkle-icon"
                        onClick={() => fieldRef.get()?.focus()}
                    />
                : null}
            </FieldOptions>
        </div>
    )

);